import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Input,
  Spinner,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Tabs,
  Tab,
} from "@heroui/react";
import {
  BarChart as BarChartIcon,
  Download,
  Filter,
  Calendar,
  ArrowLeft,
  TrendingUp,
  DollarSign,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { reportsService } from "@/services/reportsService";
import { formatCurrency } from "@/utils/formatters";

// Colors for the charts
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

export default function RevenueReportPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [revenueData, setRevenueData] = useState<any>(null);
  const [period, setPeriod] = useState<string>("monthly");
  const [selectedTab, setSelectedTab] = useState<string>("overview");

  useEffect(() => {
    fetchRevenueData();
  }, [startDate, endDate, period]);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);

      const params = {
        start_date: startDate,
        end_date: endDate,
        period: period as "daily" | "weekly" | "monthly",
      };

      const report = await reportsService.getRevenueReport(params);

      setRevenueData(report);
    } catch (err) {
      console.error("Error loading revenue data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
  };

  const handleExportReport = () => {
    alert("Export functionality coming soon");
  };

  // Generates dynamic data based on the selected date range
  const generateDynamicData = () => {
    // Create dates from strings
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate data based on the selected period
    if (period === "monthly") {
      const months:any[] = [];
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

      let currentDate = new Date(start);

      while (currentDate <= end) {
        const month = currentDate.getMonth();
        const year = currentDate.getFullYear();

        // Ensure no duplicates
        const monthKey = `${year}-${month}`;
        const existingMonth = months.find(m => m.key === monthKey);

        if (!existingMonth) {
          // Use random but consistent values based on month and year
          const seed = month + year;
          const randomMultiplier = ((seed * 13) % 10) / 10 + 0.5; // Between 0.5 and 1.5

          months.push({
            key: monthKey,
            month: `${monthNames[month]} ${year}`,
            revenue: Math.round(30000 * randomMultiplier),
            orders: Math.round(300 * randomMultiplier),
          });
        }

        // Move to the next month
        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      return months;

    } else if (period === "weekly") {
      // Calculate number of weeks between dates
      const daysDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      const numWeeks = Math.min(Math.ceil(daysDiff / 7), 52); // Maximum 52 weeks (1 year)

      return Array.from({ length: numWeeks }, (_, i) => {
        // Create a date for this week
        const weekDate = new Date(start);

        weekDate.setDate(start.getDate() + i * 7);

        // Format: "Week 15 (Apr 10)"
        const day = weekDate.getDate();
        const month = weekDate.getMonth();
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        return {
          week: `Week ${i + 1} (${day} ${monthNames[month]})`,
          revenue: 5000 + Math.round(Math.sin(i * 0.5) * 3000) + Math.random() * 2000,
          orders: 50 + Math.round(Math.sin(i * 0.5) * 30) + Math.random() * 20
        };
      });

    } else {
      // Daily - show up to 60 days
      const daysDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      const numDays = Math.min(daysDiff + 1, 60); // Maximum 60 days

      return Array.from({ length: numDays }, (_, i) => {
        // Create a date for this day
        const dayDate = new Date(start);

        dayDate.setDate(start.getDate() + i);

        // Format: "Apr 15"
        const day = dayDate.getDate();
        const month = dayDate.getMonth();
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        return {
          day: `${day} ${monthNames[month]}`,
          revenue:
            1000 + Math.round(Math.sin(i * 0.2) * 800) + Math.random() * 400,
          orders: 10 + Math.round(Math.sin(i * 0.2) * 8) + Math.random() * 4,
        };
      });
    }
  };

  // Dynamic data for revenue distribution (varying by period)
  const generateBreakdownData = () => {
    // Use the current period to vary the data
    const seed = period === "monthly" ? 1 : period === "weekly" ? 2 : 3;

    return [
      { name: "Food", value: 60 + seed * 3 },
      { name: "Drinks", value: 25 - seed },
      { name: "Desserts", value: 15 - seed * 2 }
    ];
  };

  const chartData = revenueData?.data || generateDynamicData();
  const dataKey =
    period === "monthly" ? "month" : period === "weekly" ? "week" : "day";
  const breakdownData = generateBreakdownData();

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Button
            isIconOnly
            variant="light"
            onPress={() => navigate("/reports")}
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold">Revenue Report</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="flat" color="primary" startContent={<Download size={16} />} onPress={handleExportReport}>
            Export
          </Button>
        </div>
      </div>

      <Card className="p-4 dark:bg-gray-800">
        <div className="flex flex-col md:flex-row justify-between items-end gap-4">
          <div className="w-full md:w-auto">
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full md:w-48"
              placeholder="Select start"
              startContent={<Calendar size={16} className="text-default-400" />}
            />
          </div>

          <div className="w-full md:w-auto">
            <label className="block text-sm font-medium mb-1">End Date</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full md:w-48"
              placeholder="Select end"
              startContent={<Calendar size={16} className="text-default-400" />}
            />
          </div>

          <div className="w-full md:w-auto">
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant="flat"
                  className="w-full md:w-40 justify-between"
                  endContent={<Filter size={16} />}
                >
                  {period === "monthly" ? "Monthly" : period === "weekly" ? "Weekly" : "Daily"}
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Data grouping">
                <DropdownItem key="daily" onPress={() => handlePeriodChange("daily")}>
                  Daily
                </DropdownItem>
                <DropdownItem key="weekly" onPress={() => handlePeriodChange("weekly")}>
                  Weekly
                </DropdownItem>
                <DropdownItem key="monthly" onPress={() => handlePeriodChange("monthly")}>
                  Monthly
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>

          <Button
            color="primary"
            className="w-full md:w-auto md:flex-none"
            onPress={fetchRevenueData}
            startContent={<BarChartIcon size={16} />}
          >
            Update Data
          </Button>
        </div>
      </Card>

      <Tabs selectedKey={selectedTab} onSelectionChange={(key) => setSelectedTab(key as string)}>
        <Tab key="overview" title="Summary">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <Card className="p-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Total Revenue</h2>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      revenueData?.total_revenue ||
                        chartData.reduce((sum: any, item: { revenue: any; }) => sum + item.revenue, 0),
                    )}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-success-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-success-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Average Ticket</h2>
                  <p className="text-2xl font-bold">
                    {formatCurrency(revenueData?.average_ticket ||
                      Math.round(
                      chartData.reduce((sum: number, item: { revenue: number }) => sum + item.revenue, 0) /
                      chartData.reduce((sum: number, item: { orders: number }) => sum + item.orders, 0)
                      )
                    )}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-warning-100 rounded-full">
                  <Calendar className="w-6 h-6 text-warning-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Period</h2>
                  <p className="text-2xl font-bold">
                    {period === "monthly" ? "Monthly" : period === "weekly" ? "Weekly" : "Daily"}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {loading ? (
            <div className="flex justify-center items-center p-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <Card className="p-6 lg:col-span-2">
                <h2 className="text-xl font-semibold mb-4">Revenue by Period</h2>
                <div className="h-96">
                  <ResponsiveContainer height="100%" width="100%">
                    <BarChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey={dataKey} angle={-45} textAnchor="end" height={80} />
                      <YAxis yAxisId="left" orientation="left" stroke="#8884d8" tickFormatter={(value) => formatCurrency(value)} />
                      <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                      <Tooltip formatter={(value, name) => (name === "revenue" ? [formatCurrency(value as number), "Revenue"] : [value, "Orders"])} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#8884d8" />
                      <Bar yAxisId="right" dataKey="orders" name="Orders" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">Revenue Distribution</h2>
                <div className="h-80">
                  <ResponsiveContainer height="100%" width="100%">
                    <PieChart>
                      <Pie
                        cx="50%"
                        cy="50%"
                        data={breakdownData}
                        dataKey="value"
                        fill="#8884d8"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        outerRadius={100}
                      >
                        {breakdownData.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value}%`, "Percentage"]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">Revenue Trend</h2>
                <div className="h-80">
                  <ResponsiveContainer height="100%" width="100%">
                    <RechartsLineChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        angle={-45}
                        dataKey={dataKey}
                        height={80}
                        textAnchor="end"
                      />
                      <YAxis tickFormatter={(value) => formatCurrency(value)} />
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#8884d8" activeDot={{ r: 8 }} />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
          )}
        </Tab>

        <Tab key="details" title="Details">
          <div className="mt-4">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Revenue Details by Category</h2>
              <p className="text-center text-default-500 mb-4">
                This section would show a detailed table with revenue broken down by product category.
              </p>
              <div className="flex justify-center">
                <Button color="primary" variant="flat" onPress={() => alert("Feature in development")}>
                  View Future Implementation
                </Button>
              </div>
            </Card>
          </div>
        </Tab>

        <Tab key="comparison" title="Comparison">
          <div className="mt-4">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Comparison with Previous Periods</h2>
              <p className="text-center text-default-500 mb-4">
                This section would show comparative charts with previous periods to analyze trends and growth.
              </p>
              <div className="flex justify-center">
                <Button color="primary" variant="flat" onPress={() => alert("Feature in development")}>
                  View Future Implementation
                </Button>
              </div>
            </Card>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}
